import {Button} from '@dropbox/dig-components/buttons';
import {Dropzone} from '@dropbox/dig-components/dropzone';
import {UIIcon} from '@dropbox/dig-icons';
import {UploadFolderLine, UploadFolderPictogram} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {DragEvent} from 'react';

import {AppPublishActions, getAppPublishProgressLabel} from './AppPublisher';
import {AppPublishResult, ExistingSitePublishTarget} from './publishApp';
import {
  directoryInputProps,
  formatUploadBytes,
  formatUploadValidationErrorLabel,
  useUploadDropzoneFlow,
} from './uploadDropzoneShared';

export const UploadDropzone = ({
  existingSite,
  onPublishSuccess,
}: {
  existingSite?: ExistingSitePublishTarget;
  onPublishSuccess: (result: AppPublishResult) => void;
}) => {
  const {
    client,
    inputRef,
    issues,
    openPicker,
    publisher,
    readyUpload,
    stageDroppedFiles,
    stageFiles,
    validation,
  } = useUploadDropzoneFlow({existingSite, onPublishSuccess});

  const isPublishing = publisher.isPublishing || publisher.status === 'success';
  const isPublishError = publisher.status === 'error' && Boolean(publisher.error);
  const hasValidationIssues = issues.length > 0;
  const idleLabel = t('apps_upload_dropzone_idle_label');
  const stagedLabel = readyUpload
    ? t('apps_upload_ready_description', {
        name: readyUpload.name,
        slug: readyUpload.slug,
        count: readyUpload.files.length,
        size: formatUploadBytes(readyUpload.totalBytes),
      })
    : idleLabel;
  const progressLabel = getAppPublishProgressLabel(publisher.status, publisher.progress);

  const errorLabel = isPublishError
    ? publisher.error
    : hasValidationIssues
      ? formatUploadValidationErrorLabel(issues)
      : '';

  const uploadProgress =
    isPublishing && publisher.progress?.phase === 'uploading' && publisher.progress.total > 0
      ? Math.round((publisher.progress.completed / publisher.progress.total) * 100)
      : undefined;

  const handleDrop = (event: DragEvent) => {
    if (isPublishing) {
      return;
    }
    void stageDroppedFiles(event.dataTransfer);
  };

  return (
    <>
      <Dropzone
        data-testid="apps-upload-dropzone"
        errorLabel={errorLabel}
        hasAnimatedBorderOnHover
        isError={isPublishError || hasValidationIssues}
        isUploading={isPublishing}
        label={stagedLabel}
        pictogram={UploadFolderPictogram}
        uploadProgress={uploadProgress}
        uploadProgressLabel={progressLabel}
        onDrop={handleDrop}
        withCTA={
          isPublishing ? null : (
            <>
              <Button
                onClick={openPicker}
                variant="outline"
                withIconStart={<UIIcon src={UploadFolderLine} />}
              >
                {t('apps_upload_select_folder')}
              </Button>
              <input
                {...directoryInputProps}
                ref={inputRef}
                type="file"
                multiple
                aria-label={t('apps_upload_input_label')}
                onChange={(event) => stageFiles(event.target.files)}
                style={{display: 'none'}}
              />
            </>
          )
        }
      />

      <AppPublishActions
        hasValidation={Boolean(validation)}
        publisher={publisher}
        readyToPublish={Boolean(readyUpload && client)}
      />
    </>
  );
};
